import { COMPLIANCE_BLACKLIST_PATH } from 'config/api.routes';
import services from 'service/axios';
import { handleResponse } from 'utils/responseTransformer';

import type {
  BlackListLastResponse,
  BlackListLogResponse,
  BlackListMatchesResponse,
  BlackListRequest,
  BlackListResponse,
  BlackListValidationResponse,
} from 'commons/modules/service/black-list';
import type { GenericResponse } from 'commons/modules/generic-response';
import type { Pagination } from 'commons/modules/pagination';
import type { RestrictedSearchValues } from 'components/Compliance/Restricted/BlackListSearchModal/models/restricted-search-values';

let controlador: AbortController | null;
let controladorLog: AbortController | null;
let controladorLogLast: AbortController | null;

export const getBlackListMatches = async (
  pagination: Pagination,
  requestParams: {
    status: string;
    fullName: string;
    processId: string | undefined;
  },
): Promise<GenericResponse<BlackListMatchesResponse>> => {
  controlador?.abort();
  controlador = new AbortController();
  const { signal } = controlador;
  const params = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    status: requestParams.status || null,
    fullName: requestParams.fullName || null,
    processId: requestParams.processId || null,
  };

  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/matches`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params,
      signal,
    });
    const { data, status } = res;
    controlador = null;
    return handleResponse(data, status, true);
  } catch (error: any) {
    controlador = null;
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error al obtener informaci\u00F3n',
    );
  }
};

export const getBlackListPerson = async (
  values: RestrictedSearchValues,
): Promise<GenericResponse<BlackListResponse>> => {
  const params = {
    fullName: values.fullName,
    curp: values.curp,
    rfc: values.rfc,
    clientId: values.clientId,
  };

  try {
    const res = await services.wt_api(
      `${COMPLIANCE_BLACKLIST_PATH}/natural/person`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        params,
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error al obtener informaci\u00F3n',
    );
  }
};

export const saveBlackListMatches = async (
  body: BlackListRequest,
): Promise<GenericResponse<any>> => {
  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/matches`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(body),
    });
    const { data, status } = res;
    controlador = null;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error guardar la informaci\u00F3n',
    );
  }
};

export const uploadDocumnetsBlackList = async (request: {
  file: any;
  processId: string;
  sequentialId: number;
  comments: string;
}): Promise<GenericResponse<any>> => {
  try {
    const formdata = new FormData();
    formdata.append('file', request.file);
    formdata.append('processId', request.processId);
    formdata.append('sequentialId', `${request.sequentialId}`);
    formdata.append('comments', request.comments);

    const res = await services.wt_api(
      `${COMPLIANCE_BLACKLIST_PATH}/validations`,
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
        data: formdata,
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error guardar la informaci\u00F3n',
    );
  }
};

export const getDocumnetsBlackList = async (
  processId: number,
  sequentialId: number,
): Promise<GenericResponse<BlackListValidationResponse>> => {
  const params = {
    processId,
    sequentialId,
  };
  try {
    const res = await services.wt_api(
      `${COMPLIANCE_BLACKLIST_PATH}/validations/id`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
        params,
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error obtener la informaci\u00F3n',
    );
  }
};

export const getBlackListLogs = async (
  pagination: Pagination,
  request: { status: string },
): Promise<GenericResponse<BlackListLogResponse>> => {
  controladorLog?.abort();
  controladorLog = new AbortController();
  const { signal } = controladorLog;
  const params = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    status: request.status,
  };

  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/log`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params,
      signal,
    });
    const { data, status } = res;
    controladorLog = null;
    return handleResponse(data, status, true);
  } catch (error: any) {
    controladorLog = null;
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error al obtener informaci\u00F3n',
    );
  }
};

export const execBlackList = async (): Promise<GenericResponse<any>> => {
  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/exec`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error al obtener informaci\u00F3n',
    );
  }
};

export const getBlackListLogLast = async (): Promise<
  GenericResponse<BlackListLastResponse>
> => {
  controladorLogLast?.abort();
  controladorLogLast = new AbortController();
  const { signal } = controladorLogLast;
  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/log/last`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal,
    });
    const { data, status } = res;
    controladorLogLast = null;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error al obtener informaci\u00F3n',
    );
  }
};

export const dowloandDocumnetsBlackList = async (
  processId: number,
  sequentialId: number,
) => {
  const params = {
    processId,
    sequentialId,
  };
  try {
    const res = await services.wt_api(`${COMPLIANCE_BLACKLIST_PATH}/download`, {
      method: 'GET',
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
      params,
    });
    const { data, status, headers } = res;
    return handleResponse(data, status, true, null, null, undefined, headers);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      error?.response?.data?.error,
      'Ocurrió un error obtener la informaci\u00F3n',
    );
  }
};
